'use client';

import React from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';
import { Breadcrumbs } from '@/components/ui-components/Breadcrumbs';
import { Skeleton } from '../Skeleton/Skeleton';

const Container = styled.div`
  background: ${theme.colors.white};
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const GridContainer = styled.div`
  padding: 40px 24px 0;
  overflow-x: auto;
  margin-top: auto;
  padding-bottom: 8px;
`;

const GridWrapper = styled.div`
  display: flex;
  min-width: 100%;
`;

const Grid = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  position: relative;
  align-items: start;
  column-gap: 16px;
  padding: 0 14px;
`;

const YAxisLabel = styled.div`
  grid-column: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-right: 12px;
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.gray[700]};
  gap: 8px;
  justify-content: center;
`;

const YAxisTitle = styled.div`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.gray[900]};
`;

const YAxisTicksContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-right: 8px;
  width: fit-content;
`;

const XAxisLabelsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px 20px;
  padding-left: 97px;
  padding-right: 40px;
  gap: 16px;
`;

const GridCellSkeleton = styled(Skeleton)`
  width: 20px;
  height: 20px;
  border-radius: 4px;
`;

export interface FunctionalElementChecksGridSkeletonProps {
  /** Number of element columns to show */
  columns?: number;
  /** Number of check rows to show */
  rows?: number;
  /** Title for the skeleton */
  title?: string;
}

/**
 * FunctionalElementChecksGridSkeleton Component
 * 
 * Loading skeleton for FunctionalElementChecksGrid component
 */
export const FunctionalElementChecksGridSkeleton: React.FC<FunctionalElementChecksGridSkeletonProps> = ({
  columns = 3,
  rows = 10,
  title = 'FUNCTIONAL ELEMENT CHECKS',
}) => {
  return (
    <Container>
      <Breadcrumbs
        items={[{ label: title }]}
        accentColor={theme.colors.primary.accent}
        style={{ padding: '24px' }}
      />
      
      <GridContainer>
        <GridWrapper>
          {/* Y-axis label with "Last 10 Checks" title and row numbers */}
          <YAxisLabel>
            <YAxisTitle>Last 10 Checks</YAxisTitle>
          </YAxisLabel>
          
          <YAxisTicksContainer>
            {Array.from({ length: rows }).map((_, index) => {
              // Invert the order: show 10, 9, 8, ... 1
              const displayNumber = rows - index;
              return (
                <div 
                  key={index} 
                  style={{ 
                    display: 'flex',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '12px', 
                    fontWeight: '400', 
                    color: theme.colors.gray[700], 
                    marginBottom: '8px', 
                    height: '20px',
                    width: '20px',
                  }}
                >
                  <Skeleton width="12px" height="12px" variant="circular" />
                </div>
              );
            })}
          </YAxisTicksContainer>

          <Grid>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <div 
                key={colIndex} 
                style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '8px', 
                  width: 'fit-content' 
                }}
              >
                {Array.from({ length: rows }).map((_, rowIndex) => (
                  <GridCellSkeleton
                    key={`cell-${colIndex}-${rowIndex}`}
                    animation="pulse"
                  />
                ))}
              </div>
            ))}
          </Grid>
        </GridWrapper>
      </GridContainer>

      {/* X-axis labels (column headers) */}
      <XAxisLabelsContainer>
        {Array.from({ length: columns }).map((_, index) => (
          <div
            key={`label-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '500',
              color: theme.colors.gray[700],
              writingMode: 'vertical-rl',
              minWidth: '20px',
              transform: 'rotate(180deg)',
              textOrientation: 'mixed',
              whiteSpace: 'nowrap',
            }}
          >
            <Skeleton width="60px" height="12px" />
          </div>
        ))}
      </XAxisLabelsContainer>
    </Container>
  );
};

export default FunctionalElementChecksGridSkeleton;

